import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { post } from "@/lib/api";
import { clock } from "@/lib/format";
import { entityHref, initials, subject, summaryFor, valueText } from "@/lib/history";
import type { HistoryActor, HistoryEvent } from "@/lib/types";
import { cn } from "@/lib/utils";

/** Round marker for an actor: „FB“ muted for Florian, initials on primary for agents, an empty ring for system jobs. */
export function Avatar({ actor, className, children }: { actor: Pick<HistoryActor, "kind" | "name">; className?: string; children?: React.ReactNode }) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
        actor.kind === "agent" ? "bg-primary text-primary-foreground" : actor.kind === "user" ? "bg-muted text-foreground" : "ring-1 ring-border ring-inset",
        className,
      )}
    >
      {children ?? initials(actor)}
    </span>
  );
}

const linkClass = "rounded-sm font-medium underline decoration-border underline-offset-2 transition-colors duration-150 hover:decoration-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring";

export function HistoryEventRow({ event: e }: { event: HistoryEvent }) {
  const qc = useQueryClient();
  // Undo changes the entity too, so every cached list (bookings, companies, …) is refreshed, not only the history.
  const undo = useMutation({
    mutationFn: () => post<{ undone: number }>(`/api/history/${e.id}/undo`),
    onSuccess: () => { toast.success("Rückgängig gemacht"); qc.invalidateQueries(); },
    onError: (err) => toast.error(err.message),
  });
  const href = entityHref(e);
  const who = subject(e.actor);
  const summary = summaryFor(e);
  const via = e.actor.operation ? `${e.actor.channel} · ${e.actor.operation}` : e.actor.channel;
  const time = clock(e.at);

  return (
    <li className="grid grid-cols-[2.75rem_1.75rem_minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1 border-t px-4 py-2.5 first:border-t-0 max-sm:grid-cols-[1.75rem_minmax(0,1fr)]">
      <time dateTime={e.at} className="pt-1.5 text-xs text-muted-foreground tabular-nums max-sm:hidden">{time}</time>
      <Avatar actor={e.actor} />
      <div className="flex min-w-0 flex-col gap-1 pt-1">
        <p className="line-clamp-2 text-sm leading-snug" title={`${who} ${summary} ${e.label}`}>
          <strong className="font-semibold">{who}</strong> {summary}{" "}
          {href ? <Link to={href} className={linkClass}>{e.label}</Link> : <span className="font-medium">{e.label}</span>}
        </p>
        {e.changes.map((c) => {
          const before = valueText(c.before);
          const after = valueText(c.after);
          return (
            <p key={c.field} className="text-[0.8125rem] break-words max-sm:line-clamp-2 sm:truncate leading-relaxed text-muted-foreground" title={`${c.label}: ${before} → ${after}`}>
              {c.label}: <del className="rounded-sm bg-diff-del px-1 text-diff-del-fg">{before}</del>
              {" → "}
              <ins className="rounded-sm bg-diff-add px-1 text-diff-add-fg no-underline">{after}</ins>
            </p>
          );
        })}
        <span className="flex min-w-0 items-center gap-2">
          <span className="inline-flex h-5 min-w-0 items-center rounded bg-muted px-1.5 font-mono text-[0.6875rem] text-muted-foreground">
            <span className="truncate">{via}</span>
          </span>
          <time dateTime={e.at} className="text-xs text-muted-foreground tabular-nums sm:hidden">{time}</time>
        </span>
      </div>
      {e.undoable && (
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground pointer-coarse:h-11 max-sm:col-start-2 max-sm:-ml-3 max-sm:justify-self-start"
          disabled={undo.isPending}
          onClick={() => undo.mutate()}
        >
          Rückgängig
        </Button>
      )}
    </li>
  );
}
